import React, { Fragment, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import DialogBoxComponent from "../../../components/DialogBoxComponent";
import CircularProgress from "@mui/material/CircularProgress";
import { Stack } from "@mui/material";
import {
  fetchAppendixImportList,
  createAppendixList,
  updateAppendixList,
} from "./helper";
import { useUserContext } from "../../../context/auth/UserContext";
import { useRiskManagementContext } from "../../../context/RiskManagement/RiskManagementContext";

export const AppendixImport = (props: {
  openDialog: boolean;
  tableData: any[];
  handleCancel: () => void;
  refreshList: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checked, setChecked] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [importAppendixList, setImportAppendixList] = useState<any[]>([]);
  const [existingAppendix, setExsistingAppendix] = useState<any[]>([]);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openImportConfirmation: false,
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const { tabValue, selectedRiskManagement } = useRiskManagementContext();

  const handleClose = () => {
    if (props.handleCancel) {
      props.handleCancel();
    }
  };

  const handleCloseDialog = () => {
    if (dialogState.title !== "Error") {
      setDialogState({
        ...dialogState,
        openImportConfirmation: false,
        openSuccessDialogBox: false,
      });
      props.refreshList();
      handleClose();
    } else {
      setDialogState({
        ...dialogState,
        openImportConfirmation: false,
        openSuccessDialogBox: false,
      });
    }
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setChecked([]);
    } else {
      setSelectAll(true);
      const newChecked = importAppendixList.map((ele) => ele.templateID);
      setChecked(newChecked);
    }
  };

  const getSelectedAppendix = () => {
    return importAppendixList.filter((ele) => checked.includes(ele.templateID));
  };

  const getExistingAppendix = (selectedAppendix: any[]) => {
    return selectedAppendix
      .filter((appendix: any) => {
        return props.tableData.some((ele: any) => ele.title === appendix.title);
      })
      .map((appendix: any) => {
        const matchedAppendix: any = props.tableData.find(
          (ele: any) => ele.title === appendix.title
        );
        return {
          ...appendix,
          ...(tabValue === 0
            ? {
                csRiskManagementPlanID: matchedAppendix?.csRiskManagementPlanID,
                csRiskManagementPlanAppendixID:
                  matchedAppendix.csRiskManagementPlanAppendixID,
              }
            : {
                supplyChainPlanID: matchedAppendix?.supplyChainPlanID,
                scRiskManagementPlanAppendixID:
                  matchedAppendix.scRiskManagementPlanAppendixID,
              }),
        };
      });
  };

  const handleImport = async () => {
    const selectedAppendix = getSelectedAppendix();
    const isExsistingAppendix = await getExistingAppendix(selectedAppendix);
    setExsistingAppendix(isExsistingAppendix);
    // console.log("handleImport", isExsistingAppendix);
    if (isExsistingAppendix.length > 0) {
      const existingAppendixNames = isExsistingAppendix
        .map((ele) => ele.title)
        .join(", ");
      setDialogState({
        title: "Confirm",
        body: `The following appendix are already existing in the list: <strong>${existingAppendixNames}</strong>. If you import, they will be overwritten. Are you sure?`,
        action: "Import",
        actionCancel: "Cancel",
        openImportConfirmation: true,
        openSuccessDialogBox: false,
      });
    } else {
      importAppendix();
    }
  };

  const importAppendix = async () => {
    setIsLoading(true);
    const selectedAppendix = getSelectedAppendix();
    const newlyImportAppendix = selectedAppendix.filter(
      (appendix) =>
        !existingAppendix.some((ele) => ele.title === appendix.title)
    );

    let hasError = false;
    const date = new Date();

    //Import new Appendix
    if (newlyImportAppendix.length > 0) {
      const payload = newlyImportAppendix.map((ele) => {
        return {
          content: ele.content,
          title: ele.title,
          version: "1.1",
          status: "Not Approved",
          approvedDate: null,
          approvedBy: 0,
          createDate: date,
          createdBy: userId,
          companyProfileID: companyProfileID,
          ...(tabValue === 0
            ? {
                csRiskManagementPlanID:
                  selectedRiskManagement?.csRiskManagementPlanID,
              }
            : {
                supplyChainPlanID: selectedRiskManagement?.supplyChainPlanID,
              }),
        };
      });
      // console.log("payload importAppendix", payload);
      const resImport = await createAppendixList(tabValue === 0, payload);
      if (!resImport.succeeded) {
        hasError = true;
      }
    }

    //Update existing Appendix
    if (existingAppendix.length > 0) {
      const payload = existingAppendix.map((ele) => {
        return {
          content: ele.content,
          title: ele.title,
          version: "1.1",
          status: "Not Approved",
          approvedDate: null,
          approvedBy: 0,
          updateDate: date,
          updateBy: userId,
          companyProfileID: companyProfileID,
          ...(tabValue === 0
            ? {
                csRiskManagementPlanID:
                  selectedRiskManagement?.csRiskManagementPlanID,
                csRiskManagementPlanAppendixID:
                  ele.csRiskManagementPlanAppendixID,
              }
            : {
                supplyChainPlanID: selectedRiskManagement?.supplyChainPlanID,
                scRiskManagementPlanAppendixID:
                  ele.scRiskManagementPlanAppendixID,
              }),
        };
      });
      const resUpdate = await updateAppendixList(tabValue === 0, payload);
      if (!resUpdate.succeeded) {
        hasError = true;
      }
    }

    if (hasError) {
      setIsLoading(false);
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openImportConfirmation: false,
        openSuccessDialogBox: true,
      });
    } else {
      setIsLoading(false);
      setDialogState({
        title: "Success",
        body: "Appendix successfully imported",
        action: "OK",
        actionCancel: "",
        openImportConfirmation: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const fetchAppendixImport = async () => {
    try {
      const res = await fetchAppendixImportList(tabValue === 0);
      setImportAppendixList(res);
    } catch (error) {
      console.log("@@Error:", error);
    }
  };

  useEffect(() => {
    setOpen(props.openDialog);
    if (props.openDialog) {
      fetchAppendixImport();
    }
  }, [props.openDialog]);

  return (
    <Fragment>
      <Dialog open={open} onClose={handleClose} keepMounted>
        <DialogTitle sx={{ fontSize: "1.1rem" }}>Import Appendix</DialogTitle>
        <DialogContent>
          <Stack direction="row" sx={{ mx: 2 }}>
            <Checkbox
              size="small"
              edge="start"
              checked={selectAll}
              onChange={handleSelectAll}
              tabIndex={-1}
              disableRipple
              sx={{ p: 0, mr: 3 }}
            />
            <span>Select All</span>
          </Stack>
          <DialogContentText sx={{ height: "350px", overflowY: "scroll" }}>
            <List sx={{ width: "300px", bgcolor: "background.paper" }}>
              {!isLoading || importAppendixList.length > 0 ? (
                importAppendixList.map((ele) => {
                  const labelId = `checkbox-list-label-${ele}`;

                  return (
                    <ListItem
                      key={ele.templateID}
                      disablePadding
                      disableGutters
                    >
                      <ListItemButton
                        role={undefined}
                        onClick={handleToggle(ele.templateID)}
                        dense
                      >
                        <ListItemIcon sx={{ minWidth: "2rem" }}>
                          <Checkbox
                            size="small"
                            edge="start"
                            checked={checked.indexOf(ele.templateID) !== -1}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ "aria-labelledby": labelId }}
                            sx={{ p: 0 }}
                          />
                        </ListItemIcon>
                        <ListItemText id={labelId} primary={ele.title} />
                      </ListItemButton>
                    </ListItem>
                  );
                })
              ) : (
                <div>
                  <CircularProgress />
                </div>
              )}
            </List>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "0.5rem 1.5rem" }}>
          <Button
            size="small"
            variant="contained"
            className="table-btn"
            disabled={isLoading}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={checked.length === 0 || isLoading}
            onClick={handleImport}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      {
        //Confirmation
        <DialogBoxComponent
          openDialogBox={dialogState.openImportConfirmation}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={importAppendix}
          handleCancel={handleCloseDialog}
        />
      }
      {
        //success
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
